import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Copy, Plus } from 'lucide-react';

import {
  confirmTranslationImport, loadBundles, loadTranslationCatalogue, loadTranslations, previewTranslationImport,
  saveTranslation, updateProduct,
} from './api';
import {
  Failed, Loading, Pill, XomicsSelect,
} from './parts';
import { Field, errorMessage, fieldAria } from './ui';
import { tx } from './i18n';
import { Switch } from './ProductOptions';
import ProductPageLive from './ProductPageLive';
import ProductPageSections, { SECTION_FIELDS } from './ProductPageSections';

// A product's page on the storefront, written here.
//
// On the right is the real page. On the left are its words, in the order the
// page shows them, each part a card that opens. Pointing at a part of the
// page opens its field; writing in a field shows on the page at once, and is
// on the storefront once it is saved.
//
// The words are keys in the storefront site text, storefront.site.product_
// <id>.<field>, which XOmics creates in English, empty, with the product;
// they are read and written through the translation endpoints, one language
// at a time, like the Translations page. A language's empty field shows the
// English on the storefront. Only the parts the product's kind of page draws
// are listed (the storefront's src/site/productTop.js and its page files).

const SITE_TEXT = 'storefront_site';
const ENGLISH = 'en';
const DETAILS = 6;
const FACTS = 3;

// The storefront's address, in front of a page's path.
export const SHOP_DOMAIN = 'phenomelongevity.com';

// A page's address on the storefront (a product's, a category's; XOmics works
// the path out from the English names) as a wide read-only field with a copy button.
export function ShopAddressField({ id, path, hint = tx('ui.products.page.address_hint') }) {
  const [copied, setCopied] = useState(false);
  if (!path) return null;
  const address = `${SHOP_DOMAIN}${path}`;
  async function copy() {
    try {
      await navigator.clipboard.writeText(address);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1400);
    } catch { /* the clipboard is unavailable; the field can still be selected */ }
  }
  return (
    <div className="xo-product-field-wide">
      <Field id={id} label={tx('ui.products.page.address')} hint={hint}>
        <div className="xo-pm-address-field">
          <input {...fieldAria(id, { hint: true })} value={address} readOnly onFocus={(event) => event.target.select()} />
          <button type="button" className="xo-btn-2" onClick={copy}>
            {copied ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
            {copied ? tx('ui.products.page.copied') : tx('ui.products.page.copy')}
          </button>
        </div>
      </Field>
    </div>
  );
}

let catalogue = null;
function translationCatalogue() {
  catalogue = catalogue || loadTranslationCatalogue().catch((error) => { catalogue = null; throw error; });
  return catalogue;
}

// Every value a product's page has in one language: the list comes a page at
// a time, and a product has more fields than one page holds.
const PAGE = 200;
async function readLanguage(contentTypeId, languageCode, prefix) {
  const rows = [];
  for (let offset = 0; ; offset += PAGE) {
    const { translations, total } = await loadTranslations({
      contentTypeId, languageCode, q: prefix, limit: PAGE, offset,
    });
    rows.push(...translations);
    if (translations.length < PAGE || rows.length >= Number(total ?? 0)) break;
  }
  return new Map(rows.filter((row) => row.translationKey.startsWith(prefix))
    .map((row) => [row.translationKey.slice(prefix.length), row]));
}

const LAYOUT_BY_TYPE = { kit: 'kit', wearable: 'wearable' };
const numbered = (count, ...names) => Array.from({ length: count }, (_, index) => names.map((name) => name.replace('#', index + 1))).flat();

// The parts of the page above its sections, by the kind of page: each part's
// fields, in the order the page shows them. `sections` is where the sections
// under the top go (ProductPageSections.js).
const FACT_FIELDS = numbered(FACTS, 'fact_#_value', 'fact_#_label');
const DETAIL_FIELDS = numbered(DETAILS, 'detail_#_title', 'detail_#_text');
const PARTS = {
  standard: [
    ['top', ['name', 'kicker', 'intro', 'claim', 'story', ...FACT_FIELDS]],
    ['buy', ['price_note', 'buy_note', 'bar_note']],
    ['folds', DETAIL_FIELDS],
    ['specs', ['specs_title', 'specs', 'specs_note']],
    ['sections', []],
    ['card', ['tagline', 'highlights']],
    ['bundles', []],
    ['lists', []],
  ],
  kit: [
    ['top', ['name', 'claim', 'intro', 'tagline', 'highlights']],
    ['buy', ['price_note', 'buy_note', 'bar_note']],
    ['sections', []],
    ['bundles', []],
    ['lists', []],
  ],
  // A wearable asks for a finish and a size first, so it is in no bundle.
  wearable: [
    ['top', ['name', 'kicker', 'tagline', 'claim', 'intro', 'story', ...FACT_FIELDS]],
    ['buy', ['price_note', 'buy_note', 'bar_note', 'choice_note', 'highlights']],
    ['folds', DETAIL_FIELDS],
    ['specs', ['specs_title', 'specs', 'specs_note']],
    ['lists', []],
  ],
};
// Every field some part lists, and the two no page draws any more; a field
// XOmics has beyond these is under "Other".
const KNOWN = new Set(['link', 'pair_text', ...Object.values(PARTS).flatMap((parts) => parts.flatMap(([, fields]) => fields))]);

// The bundles a product is in, as its page shows them: the newest first. They
// are made, changed and switched on the Bundles page.
function ProductBundles({ product, products }) {
  const [state, setState] = useState({ status: 'loading', bundles: [] });
  useEffect(() => {
    let live = true;
    loadBundles().then((bundles) => {
      if (live) setState({ status: 'ready', bundles: bundles.filter((bundle) => bundle.items.some((item) => item.productId === product.id)) });
    }).catch(() => { if (live) setState({ status: 'failed', bundles: [] }); });
    return () => { live = false; };
  }, [product.id]);
  const nameOf = (productId) => products.find((entry) => entry.id === productId)?.name ?? tx('ui.bundles.unknown_product');
  const title = (bundle) => (bundle.items.length === 1
    ? `${bundle.items[0].quantity} × ${nameOf(bundle.items[0].productId)}`
    : bundle.items.map((item) => nameOf(item.productId)).join(' + '));
  return <div className="xo-pm-bundles">
    {state.status === 'loading' && <Loading label={tx('ui.bundles.loading')} />}
    {state.status === 'failed' && <p className="xo-pm-bundles-none">{tx('ui.products.page.bundles_failed')}</p>}
    {state.status === 'ready' && state.bundles.length === 0
      && <p className="xo-pm-bundles-none">{tx('ui.products.page.bundles_none')}</p>}
    {state.bundles.length > 0 && <ul>
      {state.bundles.map((bundle) => <li key={bundle.id}>
        <span>{title(bundle)}</span>
        <Pill tone="info">{tx('ui.bundles.percent_off', { percent: String(bundle.discountBasisPoints / 100) })}</Pill>
        <Pill tone={bundle.isActive ? 'ok' : 'flat'}>{bundle.isActive ? tx('ui.bundles.on') : tx('ui.bundles.off')}</Pill>
      </li>)}
    </ul>}
    <Link className="xo-btn-2" to="/xomics/bundles">{tx('ui.products.page.bundles_open')}</Link>
  </div>;
}

export default function ProductPageText({
  product, products, mayEdit, mayEditProduct, onSaved, onDirtyChange, onOpenImages,
}) {
  const prefix = `storefront.site.product_${product.id}.`;
  const layout = LAYOUT_BY_TYPE[product.itemType] || 'standard';
  const [setup, setSetup] = useState({ status: 'loading', contentTypeId: null, languages: [], error: '' });
  const [language, setLanguage] = useState(ENGLISH);
  const [state, setState] = useState({ status: 'loading', english: new Map(), rows: new Map(), error: '' });
  const [form, setForm] = useState({});
  // Whether the page shows the subscription option: the product's own switch.
  const [subscription, setSubscription] = useState(Boolean(product.subscriptionShown));
  // The product's own description (the app and the shop's product cards): one
  // English value on the product, not a translated key.
  const [description, setDescription] = useState(product.description ?? '');
  // The field being written, what was last typed into a field, and how many
  // times the page was saved: what the page beside the fields follows.
  const [focus, setFocus] = useState(null);
  const [typed, setTyped] = useState(null);
  const [savedCount, setSavedCount] = useState(0);
  const [openDetails, setOpenDetails] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let live = true;
    translationCatalogue().then((result) => {
      if (!live) return;
      const type = result.contentTypes.find((entry) => entry.code === SITE_TEXT);
      if (!type) throw new Error(tx('ui.products.page.no_site_text'));
      const languages = result.languages.filter((entry) => entry.active)
        .sort((left, right) => (left.code === ENGLISH ? -1 : right.code === ENGLISH ? 1 : left.name.localeCompare(right.name)));
      setSetup({ status: 'ready', contentTypeId: type.id, languages, error: '' });
    }).catch((caught) => { if (live) setSetup({ status: 'failed', contentTypeId: null, languages: [], error: errorMessage(caught) }); });
    return () => { live = false; };
  }, []);

  const load = useCallback(async () => {
    if (setup.contentTypeId === null) return;
    setState((current) => ({ ...current, status: 'loading' }));
    try {
      const english = await readLanguage(setup.contentTypeId, ENGLISH, prefix);
      const rows = language === ENGLISH ? english : await readLanguage(setup.contentTypeId, language, prefix);
      setState({ status: 'ready', english, rows, error: '' });
      setForm(Object.fromEntries([...english.keys()].map((field) => [field, rows.get(field)?.value ?? ''])));
    } catch (caught) {
      setState((current) => ({ ...current, status: 'failed', error: errorMessage(caught) }));
    }
  }, [language, prefix, setup.contentTypeId]);
  useEffect(() => { load(); }, [load]);

  const stored = useMemo(() => Object.fromEntries([...state.english.keys()]
    .map((field) => [field, state.rows.get(field)?.value ?? ''])), [state]);
  const changed = Object.keys(stored).filter((field) => (form[field] ?? '').trim() !== stored[field].trim());
  const storedDescription = product.description ?? '';
  const descriptionChanged = description.trim() !== storedDescription.trim();
  const subscriptionChanged = subscription !== Boolean(product.subscriptionShown);
  const dirty = changed.length > 0 || descriptionChanged || subscriptionChanged;

  useEffect(() => { onDirtyChange?.(dirty); }, [dirty, onDirtyChange]);
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);

  const set = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setTyped({ field, value });
  };
  const english = (field) => state.english.get(field)?.value ?? '';
  const placeholder = (field) => (language === ENGLISH
    ? (field === 'name' ? product.name : '')
    : english(field) || (field === 'name' ? product.name : ''));
  const disabled = !mayEdit || saving || state.status !== 'ready';
  const id = (field) => `product-${product.id}-page-${field}`;

  function input(field, kind, label, hint) {
    const props = {
      ...fieldAria(id(field), { hint: Boolean(hint) }),
      value: form[field] ?? '',
      placeholder: placeholder(field),
      disabled,
      dir: 'auto',
      onChange: (event) => set(field, event.target.value),
    };
    return (
      <Field id={id(field)} label={label} hint={hint}>
        {kind === 'line' ? <input {...props} /> : <textarea {...props} rows={kind === 'rich' ? 7 : kind === 'list' ? 5 : 3} />}
      </Field>
    );
  }

  async function submit(event) {
    event.preventDefault();
    setError('');
    setSaving(true);
    try {
      const values = Object.fromEntries(changed.map((field) => [field, (form[field] ?? '').trim()]));
      // A value that exists in this language is edited; a missing translation
      // is added through the import endpoints, which only add under an English key.
      for (const field of changed.filter((entry) => state.rows.has(entry))) {
        await saveTranslation(state.rows.get(field).id, values[field]);
      }
      const added = changed.filter((field) => !state.rows.has(field) && values[field] !== '')
        .map((field) => ({ translationKey: `${prefix}${field}`, value: values[field] }));
      if (added.length > 0) {
        const preview = await previewTranslationImport({ contentTypeId: setup.contentTypeId, languageCode: language, rows: added });
        if (preview.changes.length > 0) {
          await confirmTranslationImport({
            contentTypeId: setup.contentTypeId, languageCode: language, changes: preview.changes, source: 'product-sheet',
          });
        }
      }
      if (descriptionChanged || subscriptionChanged) {
        await updateProduct(product.id, {
          ...(descriptionChanged ? { description: description.trim() || null } : {}),
          ...(subscriptionChanged ? { subscriptionShown: subscription } : {}),
        });
      }
      await load();
      setSavedCount((count) => count + 1);
      onSaved(tx('ui.products.page.saved', { code: product.name }));
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setSaving(false);
    }
  }

  if (setup.status === 'failed') return <Failed message={setup.error} />;
  if (setup.status === 'loading') return <Loading label={tx('ui.products.page.loading')} />;

  const detailCount = Math.max(openDetails, ...Array.from({ length: DETAILS }, (_, index) => index + 1)
    .filter((n) => (form[`detail_${n}_title`] ?? '') !== '' || (form[`detail_${n}_text`] ?? '') !== '' || english(`detail_${n}_title`) !== ''), 1);
  // What the storefront would show for a field: this language's words, or the
  // English where this language has none.
  const shown = (field) => (form[field] ?? '').trim() || (language === ENGLISH ? '' : english(field));
  // A part of the page was pointed at: its card opens and its field takes the cursor.
  const pick = (field) => {
    const detail = /^detail_(\d)_/.exec(field);
    if (detail && Number(detail[1]) > detailCount) setOpenDetails(Number(detail[1]));
    setFocus(field);
    window.setTimeout(() => {
      const target = document.getElementById(id(field));
      if (!target) return;
      for (let node = target.parentElement; node; node = node.parentElement) {
        if (node.tagName === 'DETAILS') node.open = true;
      }
      target.scrollIntoView({ block: 'center', behavior: 'smooth' });
      target.focus({ preventScroll: true });
    }, 0);
  };
  const noteFocus = (event) => {
    const match = new RegExp(`^product-${product.id}-page-(.+)$`).exec(event.target.id || '');
    if (match && match[1] !== 'language' && match[1] !== 'description') setFocus(match[1]);
  };
  const parts = PARTS[layout];
  const extra = [...state.english.keys()].filter((field) => !KNOWN.has(field) && !SECTION_FIELDS.has(field));

  // One part of the page as a card that opens: its name, what it is, and how
  // much of it is written.
  const part = (name, fields, children, { open = false } = {}) => {
    const filled = fields.filter((field) => shown(field) !== '').length;
    return <details key={name} className="xo-pm-card xo-pm-section" open={open || undefined}
      aria-labelledby={`product-${product.id}-part-${name}`}>
      <summary>
        <span className="xo-pm-section-name">
          <strong id={`product-${product.id}-part-${name}`}>{tx(`ui.products.page.part.${name}`)}</strong>
          <small>{tx(`ui.products.page.part.${name}_hint`)}</small>
        </span>
        {fields.length > 0 && <Pill tone={filled === 0 ? 'flat' : 'ok'}>
          {tx('ui.products.page.part.filled', { filled, total: fields.length })}</Pill>}
      </summary>
      <div className="xo-pm-section-body">{children}</div>
    </details>;
  };
  const has = (fields, field) => fields.includes(field);
  const drawn = {
    top: (fields) => part('top', fields, <>
      <div className="xo-product-details-grid">
        {input('name', 'line', tx('ui.products.page.name'), tx('ui.products.page.name_hint'))}
        {has(fields, 'kicker') && input('kicker', 'line', tx('ui.products.page.kicker'), tx('ui.products.page.kicker_hint'))}
        {has(fields, 'claim') && input('claim', 'line', tx('ui.products.page.claim'), tx('ui.products.page.claim_hint'))}
        {layout !== 'standard' && has(fields, 'tagline')
          && input('tagline', 'line', tx('ui.products.page.tagline'), tx('ui.products.page.badge_hint'))}
        <div className="xo-product-field-wide">{input('intro', 'text', tx('ui.products.page.intro'))}</div>
        {has(fields, 'story') && <div className="xo-product-field-wide">
          {input('story', 'text', tx('ui.products.page.story'))}</div>}
        {layout === 'kit' && has(fields, 'highlights') && <div className="xo-product-field-wide">
          {input('highlights', 'list', tx('ui.products.page.highlights'), tx('ui.products.page.one_per_line'))}</div>}
      </div>
      {has(fields, 'fact_1_value') && <>
        <h4 className="xo-pm-page-sub">{tx('ui.products.page.facts')}</h4>
        <div className="xo-pm-page-facts">
          {Array.from({ length: FACTS }, (_, index) => index + 1).map((n) => <div key={n} className="xo-pm-page-fact">
            {input(`fact_${n}_value`, 'line', tx('ui.products.page.fact_value', { number: n }))}
            {input(`fact_${n}_label`, 'line', tx('ui.products.page.fact_label', { number: n }))}
          </div>)}
        </div>
      </>}
    </>, { open: true }),
    buy: (fields) => part('buy', fields, <>
      <div className="xo-product-details-grid">
        {input('price_note', 'line', tx('ui.products.page.price_note'))}
        {input('buy_note', 'line', tx('ui.products.page.buy_note'))}
        {input('bar_note', 'line', tx('ui.products.page.bar_note'), tx('ui.products.page.bar_note_hint'))}
        {has(fields, 'choice_note') && input('choice_note', 'line', tx('ui.products.page.choice_note'))}
        {has(fields, 'highlights') && <div className="xo-product-field-wide">
          {input('highlights', 'list', tx('ui.products.page.highlights'), tx('ui.products.page.one_per_line'))}</div>}
      </div>
      <div className="xo-pm-page-switch">
        <span>
          <strong>{tx('ui.products.page.subscription')}</strong>
          <small>{tx('ui.products.page.subscription_hint')}</small>
        </span>
        <Switch on={subscription} disabled={!mayEditProduct || saving}
          label={tx('ui.products.page.subscription')} onChange={setSubscription} />
      </div>
    </>),
    folds: (fields) => part('folds', fields, <>
      {Array.from({ length: detailCount }, (_, index) => index + 1).map((n) => (
        <div key={n} className="xo-pm-page-detail">
          {input(`detail_${n}_title`, 'line', tx('ui.products.page.detail_title', { number: n }))}
          {input(`detail_${n}_text`, 'rich', tx('ui.products.page.detail_text', { number: n }))}
        </div>
      ))}
      {mayEdit && detailCount < DETAILS && (
        <button type="button" className="xo-link" disabled={disabled}
          onClick={() => setOpenDetails(detailCount + 1)}><Plus aria-hidden="true" /> {tx('ui.products.page.add_detail')}</button>
      )}
    </>),
    specs: (fields) => part('specs', fields, <div className="xo-product-details-grid">
      {input('specs_title', 'line', tx('ui.products.page.specs_title'))}
      {input('specs_note', 'line', tx('ui.products.page.specs_note'))}
      <div className="xo-product-field-wide">
        {input('specs', 'list', tx('ui.products.page.specs_rows'), tx('ui.products.page.specs_hint'))}
      </div>
    </div>),
    sections: () => <ProductPageSections key="sections" layout={layout} productId={product.id} form={form} shown={shown} set={set}
      input={input} disabled={disabled} settingsDisabled={disabled || language !== ENGLISH} mayEdit={mayEdit} />,
    card: (fields) => part('card', fields, <div className="xo-product-details-grid">
      {input('tagline', 'line', tx('ui.products.page.tagline'), tx('ui.products.page.card_line_hint'))}
      <div className="xo-product-field-wide">
        {input('highlights', 'list', tx('ui.products.page.highlights'), tx('ui.products.page.card_points_hint'))}
      </div>
    </div>),
    bundles: (fields) => part('bundles', fields, <ProductBundles product={product} products={products} />),
    lists: () => part('lists', [], <Field id={id('description')} label={tx('ui.products.page.description')}
      hint={tx('ui.products.page.description_hint')}>
      <textarea {...fieldAria(id('description'), { hint: true })} rows="4" dir="auto"
        value={description} disabled={!mayEditProduct || saving || language !== ENGLISH}
        onChange={(event) => setDescription(event.target.value)} />
    </Field>),
  };

  return (
    <div className="xo-pm-page-layout">
    <form className="xo-product-details-editor xo-pm-details xo-pm-page" onSubmit={submit} onFocus={noteFocus}>
      <section className="xo-pm-card">
        <div className="xo-pm-page-head">
          <div>
            <h3>{tx('ui.products.page.title')}</h3>
            <p>{language === ENGLISH ? tx('ui.products.page.how') : tx('ui.products.page.hint_translation')}</p>
          </div>
          <Field id={id('language')} label={tx('ui.products.page.language')}>
            <XomicsSelect {...fieldAria(id('language'))} value={language}
              disabled={saving || (dirty && state.status === 'ready')}
              onChange={(event) => setLanguage(event.target.value)}>
              {setup.languages.map((entry) => <option key={entry.code} value={entry.code}>
                {entry.nativeName && entry.nativeName !== entry.name ? `${entry.name} · ${entry.nativeName}` : entry.name}
              </option>)}
            </XomicsSelect>
          </Field>
        </div>
        {state.status === 'failed' && <Failed message={state.error} onRetry={load} />}
        {state.status === 'loading' && <Loading label={tx('ui.products.page.loading')} />}
        {error && <p className="xo-alert t-bad" role="alert">{error}</p>}
      </section>

      {state.status === 'ready' && <>
        {parts.map(([name, fields]) => drawn[name](fields))}
        {extra.length > 0 && part('other', extra, <div className="xo-product-details-grid">
          {extra.map((field) => <div key={field} className="xo-product-field-wide">{input(field, 'text', field)}</div>)}
        </div>)}
      </>}

      {dirty && (mayEdit || mayEditProduct) && (
        <div className="xo-pm-savebar">
          <span>{tx('ui.products.page.unsaved')}</span>
          <div>
            <button type="button" className="xo-btn-2" disabled={saving}
              onClick={() => {
                setForm({ ...stored }); setDescription(storedDescription);
                setSubscription(Boolean(product.subscriptionShown));
                // The page beside the fields goes back to the saved words too.
                setSavedCount((count) => count + 1);
              }}>
              {tx('ui.products.detail.discard')}
            </button>
            <button type="submit" className="xo-btn" disabled={saving}>
              {saving ? tx('ui.products.settings.saving') : tx('ui.products.settings.save')}
            </button>
          </div>
        </div>
      )}
    </form>
    {state.status === 'ready' && (
      <ProductPageLive path={product.path} focus={focus} typed={typed} saved={savedCount}
        onPick={pick} onPickPhoto={() => onOpenImages?.()} />
    )}
    </div>
  );
}
